import { type ReactNode } from "react";
import { createBrowserRouter, Navigate, type RouteObject } from "react-router-dom";

import { AppShell } from "@/components/layout/AppShell";
import { LandingRedirect, RequireDashboard } from "@/components/auth/PermissionLanding";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ProfilePage } from "@/pages/ProfilePage";
import { AuditPage } from "@/pages/AuditPage";
import { DocsPage } from "@/pages/DocsPage";
import { CanonicalPage } from "@/pages/CanonicalPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { LoginPage } from "@/pages/LoginPage";
import { SupportEnterPage } from "@/pages/SupportEnterPage";
import { MappingDetailPage } from "@/pages/MappingDetailPage";
import { MappingsPage } from "@/pages/MappingsPage";
import { MessagesPage } from "@/pages/MessagesPage";
import { GroupsPage } from "@/pages/GroupsPage";
import { UsersPage } from "@/pages/UsersPage";
import { ActivityPage } from "@/pages/ActivityPage";
import { AlertsPage } from "@/pages/AlertsPage";
import { ChannelsPage } from "@/pages/ChannelsPage";
import { DatasetsPage } from "@/pages/DatasetsPage";
import { DatasetGraphPage } from "@/pages/DatasetGraphPage";
import { DatasetSemanticsCatalogPage } from "@/pages/DatasetSemanticsCatalogPage";
import { DatasetSemanticsPage } from "@/pages/DatasetSemanticsPage";
import { KnowledgeBasesPage } from "@/pages/KnowledgeBasesPage";
import { KnowledgeBaseDetailPage } from "@/pages/KnowledgeBaseDetailPage";
import { SourcesPage } from "@/pages/SourcesPage";
import { CredentialsPage } from "@/pages/CredentialsPage";
import { ServiceAccountsPage } from "@/pages/ServiceAccountsPage";
import { DeliveriesPage } from "@/pages/DeliveriesPage";
import { RateLimitsPage } from "@/pages/RateLimitsPage";
import { IngressPage } from "@/pages/IngressPage";
import { GatewaysPage } from "@/pages/GatewaysPage";
import { TopologyPage } from "@/pages/TopologyPage";
import { TracePage } from "@/pages/TracePage";
import { AppsPage } from "@/pages/AppsPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { AiCostsPage } from "@/pages/AiCostsPage";
import { LlmConfigsPage } from "@/pages/LlmConfigsPage";
import { AgentsPage } from "@/pages/AgentsPage";
import { McpServersPage } from "@/pages/McpServersPage";
import { TeamsPage } from "@/pages/TeamsPage";
import { ToolsPage } from "@/pages/ToolsPage";
import { ToolDetailPage } from "@/pages/ToolDetailPage";
import { ExecutionsPage } from "@/pages/ExecutionsPage";
import { SandboxPage } from "@/pages/SandboxPage";
import { AppUsersPage } from "@/pages/AppUsersPage";
import { ReportDesignerPage } from "@/pages/ReportDesignerPage";
import { ReportTypesPage } from "@/pages/ReportTypesPage";
import { ParentPortalPage } from "@/pages/ParentPortalPage";
import { PortalRegistrationsPage } from "@/pages/PortalRegistrationsPage";
import { PortalSecurityPage } from "@/pages/PortalSecurityPage";
import { AppEntryPage } from "@/pages/AppEntryPage";
import { TableDesignerPage } from "@/pages/TableDesignerPage";
import { DatasetEditorPage } from "@/pages/DatasetEditorPage";
import { ComponentLibraryEditorPage } from "@/pages/ComponentLibraryEditorPage";
import { AppAccessPage } from "@/pages/AppAccessPage";
import { FormBuilderPage } from "@/pages/FormBuilderPage";
import { PageBuilderPage } from "@/pages/PageBuilderPage";
import { WorkflowsPage } from "@/pages/WorkflowsPage";
import { EtlPipelinesPage } from "@/pages/EtlPipelinesPage";
import { EtlLanesPage } from "@/pages/EtlLanesPage";
import { EtlEditorPage } from "@/pages/EtlEditorPage";
import { WorkflowRunsPage } from "@/pages/WorkflowRunsPage";
import { WorkflowEditorPage } from "@/components/workflows/WorkflowEditor";
import { AggregateBucketsPage } from "@/pages/AggregateBucketsPage";
import { PortalPageView } from "@/pages/portal/PortalPageView";
import { ParentPortalLoginPage } from "@/pages/portal/ParentPortalLoginPage";
import { ParentPortalSignupPage } from "@/pages/portal/ParentPortalSignupPage";
import { ParentPortalLauncher } from "@/pages/portal/ParentPortalLauncher";
import { PortalAccountPage } from "@/pages/portal/PortalAccountPage";
import { PortalAppEntry } from "@/pages/portal/PortalAppEntry";
import { PortalAssistantPage } from "@/pages/portal/PortalAssistantPage";
import { PortalRoot } from "@/pages/portal/PortalRoot";
import { PlatformAdminsPage } from "@/pages/platform/PlatformAdminsPage";
import { PlatformAuditPage } from "@/pages/platform/PlatformAuditPage";
import { PlatformLoginPage } from "@/pages/platform/PlatformLoginPage";
import { PlatformSecurityPage } from "@/pages/platform/PlatformSecurityPage";
import { PlatformSettingsPage } from "@/pages/platform/PlatformSettingsPage";
import { PlatformDashboardPage } from "@/pages/platform/PlatformDashboardPage";
import { PlatformShell } from "@/pages/platform/PlatformShell";
import { PlatformTenantsPage } from "@/pages/platform/PlatformTenantsPage";
import { PlatformOrganizationDetailPage } from "@/pages/platform/PlatformOrganizationDetailPage";
import { PlatformTenantDetailPage } from "@/pages/platform/PlatformTenantDetailPage";
import { PlatformOnboardingPage } from "@/pages/platform/PlatformOnboardingPage";
import { PlatformPlansPage } from "@/pages/platform/PlatformPlansPage";
import { PlatformCellsPage } from "@/pages/platform/PlatformCellsPage";
import { PlatformPartnersPage } from "@/pages/platform/PlatformPartnersPage";
import { PlatformStaffPage } from "@/pages/platform/PlatformStaffPage";
import { PlatformOrganizationsPage } from "@/pages/platform/PlatformOrganizationsPage";
import { PlatformIdentitiesPage } from "@/pages/platform/PlatformIdentitiesPage";
import { PlatformLicenseDetailPage } from "@/pages/platform/PlatformLicenseDetailPage";
import { PlatformLicensesPage } from "@/pages/platform/PlatformLicensesPage";
import { IdentityAccountPage } from "@/pages/onboarding/IdentityAccountPage";
import { OnboardingSignupPage } from "@/pages/onboarding/OnboardingSignupPage";
import { ResetPasswordPage } from "@/pages/onboarding/ResetPasswordPage";
import { ProvisioningPage } from "@/pages/onboarding/ProvisioningPage";
import { UnifiedLoginPage } from "@/pages/onboarding/UnifiedLoginPage";
import { FeatureGate } from "@/components/billing/FeatureGate";
import { isOnboardingHost, isPlatformHost } from "@/lib/platform";

/** Wrap an AI-plane page so a tenant whose plan lacks `ai_plane` sees a frosted lock
 * panel instead of the raw page (which would 402 + pop the global cap modal). ADR-0041 §6.*/
const aiGated = (el: ReactNode) => <FeatureGate feature="ai_plane">{el}</FeatureGate>;

/** The superadmin control plane — its own login + shell. Mounted at `/platform`
 * on every host (dev/apex, path-based) AND, on the `platform.<base>` subdomain,
 * promoted to the host root so the bare subdomain serves it (ADR-0036 Fase B). */
const platformChildren: RouteObject[] = [
  { index: true, element: <PlatformDashboardPage /> },
  { path: "organizations", element: <PlatformOrganizationsPage /> },
  { path: "organizations/:organizationId", element: <PlatformOrganizationDetailPage /> },
  { path: "identities", element: <PlatformIdentitiesPage /> },
  { path: "tenants", element: <PlatformTenantsPage /> },
  { path: "tenants/:id", element: <PlatformTenantDetailPage /> },
  { path: "plans", element: <PlatformPlansPage /> },
  { path: "cells", element: <PlatformCellsPage /> },
  { path: "licenses", element: <PlatformLicensesPage /> },
  { path: "licenses/:id", element: <PlatformLicenseDetailPage /> },
  { path: "onboarding", element: <PlatformOnboardingPage /> },
  { path: "partners", element: <PlatformPartnersPage /> },
  { path: "admins", element: <PlatformAdminsPage /> },
  { path: "staff", element: <PlatformStaffPage /> },
  { path: "audit", element: <PlatformAuditPage /> },
  { path: "security", element: <PlatformSecurityPage /> },
  { path: "settings", element: <PlatformSettingsPage /> },
];
const PLATFORM_PLANE: RouteObject[] = [
  { path: "/platform/login", element: <PlatformLoginPage /> },
  { path: "/platform", element: <PlatformShell />, children: platformChildren },
];

/** The self-serve onboarding plane (ADR-0041) — auth-free signup + the UNIFIED
 * login ("one door, many workspaces"). Mounted at `/onboarding[/login]` on every
 * host (dev/apex, path-based) AND, on the `app.<base>` subdomain, promoted to the
 * host root (`/` = unified login, the primary door) so the bare subdomain serves it. */
const ONBOARDING_PLANE: RouteObject[] = [
  { path: "/onboarding", element: <OnboardingSignupPage /> },
  { path: "/onboarding/login", element: <UnifiedLoginPage /> },
  // Account Tray self-service (ADR-0077 S2) — path-based mirrors of the host-root pages.
  { path: "/onboarding/account", element: <IdentityAccountPage /> },
  { path: "/onboarding/reset", element: <ResetPasswordPage /> },
  // Stripe `success_url` landing (ADR-0041 Fase 1) — polls until the paid signup provisions.
  { path: "/onboarding/provisioning", element: <ProvisioningPage /> },
];

const APP_ROUTES: RouteObject[] = [
  { path: "/login", element: <LoginPage /> },
  // Where a partner / Tray-support technician lands from Central (ADR-0081 S3).
  // OUTSIDE ProtectedRoute by necessity: the visitor has no session yet — the signed
  // permission in the URL fragment IS the credential, and this page spends it.
  { path: "/support/enter", element: <SupportEnterPage /> },
  // Portal (ADR-0010 P7) — end-user surface, SEPARATE auth realm
  // (app_users), NOT under the operator ProtectedRoute/AppShell. Served at
  // root like the rest of the SPA — see `PORTAL_PREFIX` in `lib/portalAuth.ts`.
  // Parent portal (ADR-0015) — slug-less: the tenant-branded login + the
  // app launcher. Static segments rank above the `:appSlug` routes below,
  // so `/portal/login` and `/portal/me` never collide with an app slug.
  // The bare portal front door: anonymous → login, signed-in → their single
  // app or the launcher. Without this, `/portal` fell to the `*` catch-all →
  // the operator console.
  { path: "/portal", element: <PortalRoot /> },
  { path: "/portal/login", element: <ParentPortalLoginPage /> },
  { path: "/portal/signup", element: <ParentPortalSignupPage /> },
  { path: "/portal/me", element: <ParentPortalLauncher /> },
  { path: "/portal/account", element: <PortalAccountPage /> },
  { path: "/portal/:appSlug/p/:pageSlug", element: <PortalPageView /> },
  // Readable detail URLs: `/p/<page>/<rowId>` seeds `$page.row` from the PATH
  // segment (the clean REST twin of the `?row=<id>` query). Two explicit routes
  // (not an optional `:rowId?`) so old `?row=` deep-links keep matching the base.
  { path: "/portal/:appSlug/p/:pageSlug/:rowId", element: <PortalPageView /> },
  // ADR-0049 — autonomous full-page assistant surfaces (the app's `chat`/`voice`
  // interface as its OWN front door, not a docked widget). Namespaced under `/i/`
  // ("interface") so other standalone interface kinds can share the prefix.
  { path: "/portal/:appSlug/i/chat", element: <PortalAssistantPage surface="chat" /> },
  { path: "/portal/:appSlug/i/voice", element: <PortalAssistantPage surface="voice" /> },
  // Bare app root → resolve the app's REAL home (is_home page) and redirect.
  { path: "/portal/:appSlug", element: <PortalAppEntry /> },
  // Superadmin control plane (ADR-0036 Phase 2) — a THIRD realm with its own
  // login + token store + shell, OUTSIDE the operator ProtectedRoute/AppShell.
  // Kept path-based here for dev/apex; the `platform.<base>` host root-mounts it.
  ...PLATFORM_PLANE,
  // Self-serve onboarding (ADR-0041 Fase 0) — kept path-based here for dev/apex;
  // the `app.<base>` host root-mounts it below.
  ...ONBOARDING_PLANE,
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: "/", element: <LandingRedirect /> },
          {
            path: "/dashboard",
            element: (
              <RequireDashboard>
                <DashboardPage />
              </RequireDashboard>
            ),
          },
          { path: "/settings", element: <SettingsPage /> },
          // Legacy routes folded into the unified Settings page.
          {
            path: "/odino-settings",
            element: <Navigate to="/settings?section=odino" replace />,
          },
          {
            path: "/advanced-settings",
            element: <Navigate to="/settings?section=security" replace />,
          },
          { path: "/ai/costs", element: aiGated(<AiCostsPage />) },
          { path: "/ai/llm-configs", element: aiGated(<LlmConfigsPage />) },
          { path: "/ai/agents", element: aiGated(<AgentsPage />) },
          { path: "/ai/tools", element: aiGated(<ToolsPage />) },
          { path: "/ai/tools/new", element: aiGated(<ToolDetailPage />) },
          { path: "/ai/tools/:id", element: aiGated(<ToolDetailPage />) },
          { path: "/ai/mcp-servers", element: aiGated(<McpServersPage />) },
          { path: "/ai/teams", element: aiGated(<TeamsPage />) },
          { path: "/ai/executions", element: aiGated(<ExecutionsPage />) },
          { path: "/ai/sandbox", element: aiGated(<SandboxPage />) },
          { path: "/topology", element: <TopologyPage /> },
          // Trace explorer: deep-link only — no sidebar entry, the
          // page is always reached via "View trace" buttons on
          // delivery / message / execution rows. Anchor type + id
          // come from the URL path.
          { path: "/trace/:anchorType/:anchorId", element: <TracePage /> },
          { path: "/activity", element: <ActivityPage /> },
          { path: "/alerts", element: <AlertsPage /> },
          { path: "/channels", element: <ChannelsPage /> },
          { path: "/sources", element: <SourcesPage /> },
          { path: "/datasets", element: <DatasetsPage /> },
          { path: "/knowledge-bases", element: aiGated(<KnowledgeBasesPage />) },
          { path: "/knowledge-bases/:id", element: aiGated(<KnowledgeBaseDetailPage />) },
          { path: "/datasets/graph", element: <DatasetGraphPage /> },
          { path: "/datasets/semantics", element: <DatasetSemanticsCatalogPage /> },
          { path: "/datasets/:datasetId/semantics", element: <DatasetSemanticsPage /> },
          { path: "/credentials", element: <CredentialsPage /> },
          { path: "/service-accounts", element: <ServiceAccountsPage /> },
          { path: "/rate-limits", element: <RateLimitsPage /> },
          { path: "/deliveries", element: <DeliveriesPage /> },
          { path: "/messages", element: <MessagesPage /> },
          // Legacy URL — redirect to the unified ingress page filtered
          // to webhooks. Anyone with a bookmark keeps working.
          {
            path: "/webhooks",
            element: <Navigate to="/ingress?type=webhook" replace />,
          },
          { path: "/ingress", element: <IngressPage /> },
          {
            path: "/gateways",
            element: (
              <FeatureGate feature="gateways">
                <GatewaysPage />
              </FeatureGate>
            ),
          },
          { path: "/mappings", element: <MappingsPage /> },
          { path: "/mappings/:id", element: <MappingDetailPage /> },
          { path: "/canonical", element: <CanonicalPage /> },
          { path: "/users", element: <UsersPage /> },
          { path: "/groups", element: <GroupsPage /> },
          // App users (ADR-0010 P7) — portal end-users (app_users) admin.
          { path: "/app-users", element: <AppUsersPage /> },
          { path: "/report-types", element: <ReportTypesPage /> },
          {
            path: "/report-types/:typeId/reports/:reportId/designer",
            element: <ReportDesignerPage />,
          },
          // Parent portal (ADR-0015) — tenant brand + launcher/switcher config.
          { path: "/parent-portal", element: <ParentPortalPage /> },
          // Portal Security (ADR-0016) — registration policy + 2FA + app access.
          { path: "/portal-security", element: <PortalSecurityPage /> },
          // Registrations queue (ADR-0016) — pending self-signups worklist.
          { path: "/portal-registrations", element: <PortalRegistrationsPage /> },
          { path: "/account", element: <ProfilePage /> },
          { path: "/profile", element: <ProfilePage /> },
          { path: "/audit", element: <AuditPage /> },
          { path: "/workflows", element: <WorkflowsPage /> },
          { path: "/workflows/:workflowId", element: <WorkflowEditorPage /> },
          { path: "/etl", element: <EtlPipelinesPage /> },
          { path: "/etl/lanes", element: <EtlLanesPage /> },
          { path: "/etl/:id", element: <EtlEditorPage /> },
          // Automations were merged into the unified Workflows screen.
          // Keep the old paths alive: list → redirect, editor → alias
          // (same WorkflowEditorPage, any kind) so bookmarks still work.
          { path: "/automations", element: <Navigate to="/workflows" replace /> },
          { path: "/automations/:workflowId", element: <WorkflowEditorPage /> },
          { path: "/workflow-runs", element: <WorkflowRunsPage /> },
          // Apps (ADR-0010) — low-code app layer: Data Tables, views,
          // app-kind workflows. List → detail (tables) → table designer.
          { path: "/apps", element: <AppsPage /> },
          { path: "/apps/:appId", element: <AppEntryPage /> },
          { path: "/apps/:appId/access", element: <AppAccessPage /> },
          { path: "/apps/:appId/forms/:formId", element: <FormBuilderPage /> },
          { path: "/apps/:appId/pages/:pageId", element: <PageBuilderPage /> },
          {
            // ADR-0049 Slice C — page-less builder (a chat/voce/form-only app has
            // no web page). `?iface=chat|voice|form|core` picks the landing area.
            path: "/apps/:appId/builder",
            element: <PageBuilderPage />,
          },
          { path: "/apps/:appId/tables/:tableId", element: <TableDesignerPage /> },
          { path: "/apps/:appId/datasets/:datasetId", element: <DatasetEditorPage /> },
          {
            path: "/apps/:appId/components/:componentId",
            element: <ComponentLibraryEditorPage />,
          },
          { path: "/aggregate-buckets", element: <AggregateBucketsPage /> },
          // Docs hub: section + topic via URL so deep-links inside
          // narratives/runbooks survive shares + browser back.
          { path: "/docs", element: <DocsPage /> },
          { path: "/docs/:section", element: <DocsPage /> },
          { path: "/docs/:section/:topic", element: <DocsPage /> },
        ],
      },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
];

/** On the `platform.<base>` control-plane host the operator/portal app is absent:
 * serve ONLY the superadmin plane and bounce any stray path into it, so the bare
 * subdomain root shows the control plane (ADR-0036 Fase B). */
const PLATFORM_HOST_ROUTES: RouteObject[] = [
  ...PLATFORM_PLANE,
  { path: "*", element: <Navigate to="/platform" replace /> },
];

/** On the `app.<base>` onboarding host the operator/portal app is absent: the bare
 * root is the UNIFIED LOGIN (the primary door — "one door, many workspaces"), with
 * `/onboarding` for self-serve signup; any stray path bounces to the login (ADR-0041). */
const ONBOARDING_HOST_ROUTES: RouteObject[] = [
  { path: "/", element: <UnifiedLoginPage /> },
  // `/login` is the door's CANONICAL external address (Central's SSO leg links
  // `app.<base>/login?continue=central`, ADR-0083). Without this explicit route the
  // `*` catch-all Navigate-to-"/" would DROP the query string — and with it the
  // whole reason the visitor came.
  { path: "/login", element: <UnifiedLoginPage /> },
  // Account Tray self-service at the host root (ADR-0077 S2): the account page +
  // the mailed reset link's landing.
  { path: "/account", element: <IdentityAccountPage /> },
  { path: "/reset", element: <ResetPasswordPage /> },
  ...ONBOARDING_PLANE,
  { path: "*", element: <Navigate to="/" replace /> },
];

function routesForHost(): RouteObject[] {
  if (isPlatformHost()) return PLATFORM_HOST_ROUTES;
  if (isOnboardingHost()) return ONBOARDING_HOST_ROUTES;
  return APP_ROUTES;
}

export const router = createBrowserRouter(routesForHost(), { basename: "/" });
